"use strict";

import Polygon from "ol/geom/Polygon";
import LineString from "ol/geom/LineString";
import {unByKey} from "ol/Observable";
import Feature from "ol/Feature";

/**
 * @classdesc
 * OL-Implementierung der Mess-Interaktion.
 *
 * @class
 * @extends gc.gis.module.GCMeasureImpl
 * @memberof gc.gis.module.GCMeasure
 * @author Christian Schmidt <c.schmidt@brain-scc.de>
 *
 * @param {Object} options Aufrufoptionen
 * @param {gc.gis.module.GCMeasure} options.instance Modul-Instanz
 * @param {gc.gis.map.GCMapView} [options.map={@link gc.gis.GCApplication#map}] Karten-Instanz
 */
gc.gis.module.OLMeasureImpl = function( options )
{
	gc.gis.module.GCMeasureImpl.call(this, options);

	this.ol_map = this.map.getMap().getImpl();

	this._helpTooltipStartMsg = 'Klicken zum Starten der Messung';
	this._helpTooltipPolygonMsg = 'Klicken zum Abschließen der Flächenmessung';
	this._helpTooltipLineMsg = 'Klicken um Punkt hinzuzufügen (Doppelklick zum Abschließen der Linienmessung),'
		+ '<br/>Zur Flächenmessung die Fläche schließen';

	// Interaktion zum Zeichnen der Messobjekte
	this._draw_interaction = gc.gis.iface.mapFactory.createMapDrawInteraction(this.map, {
		drawingStyle: gc.gis.iface.styleFactory.getStyleFunction(gc.gis.factory.StyleType.MEASURE),
		finalStyle: gc.gis.iface.styleFactory.getStyleFunction(gc.gis.factory.StyleType.MEASURE_FINAL),
		allowClearDrawingArea: true
	});
	//this._draw_interaction.setGeometryType(gc.gis.geom.GCGeometryType.POLYGON);
	this._draw_interaction.setGeometryType('PolygonOrLinestring');

	// Layer zum Hervorheben des markierten Segments
	this._highlightLayer = new gc.gis.layer.OLTrackingLayer(this.map.getMap(), {
		allowClearDrawingArea: true
	});
	this._highlightLayer.setStyle(gc.gis.iface.styleFactory.getStyleFunction(gc.gis.factory.StyleType.MEASURE_ACTIVE));
	this.map.getMap().addTrackingLayer(this._highlightLayer);

	this._initEvents();
};
gc.gis.module.OLMeasureImpl.prototype = Object.create(gc.gis.module.GCMeasureImpl.prototype);

/**
 * @inheritdoc
 */
gc.gis.module.OLMeasureImpl.prototype.setActive = function(state)
{
	this._draw_interaction.setActive(state === true);

	if (state)
	{
		this.map.showMouseTooltip(this._helpTooltipStartMsg);
	}
	else
	{
		this.map.disableMouseTooltip();
	}
};

/**
 * @inheritdoc
 */
gc.gis.module.OLMeasureImpl.prototype.highlightSegment = function(feature)
{
	this.resetHighlighting();
	this._highlightLayer.addFeature(feature);
};

/**
 * @inheritdoc
 */
gc.gis.module.OLMeasureImpl.prototype.resetHighlighting = function()
{
	this._highlightLayer.clear();
};

/**
 * Initialisiert die Events.
 */
gc.gis.module.OLMeasureImpl.prototype._initEvents = function()
{
	var self = this;
	// TODO Messergebnis durch ClearDrawingArea auch leeren
	// this._draw_interaction.getLayer().on(gc.gis.layer.GCVectorLayerEvent.LAYER_CLEARED, this, function (evt) {
	// 	self._fireEvent(gc.gis.module.GCMeasureEvent.LAYER_CLEARED, {});
	// });

	// Zeichnen-Aktionen aufmotzen
	var featureChangeEvtentKey,
		numPoints = -1,
		isSnappedOnFirst = false;
	this._draw_interaction.on(gc.gis.map.GCMapDrawInteractionEvent.DRAWSTART, this, function(evt) {
		try
		{
			self.clear();   // TODO anstatt alles zurückzusetzen, neue Messung beginnen und diese als neues Ergebnis zufügen

			var ol_feature = evt.feature.getImpl();
			featureChangeEvtentKey = ol_feature.on('change', function(evt2) {
				var ol_geom = evt2.target.getGeometry();
				var measure_geom = null;

				if (ol_geom instanceof Polygon)
				{
					// das sketchPolygon ist stets geschlossen, daher die letzte Koordinate löschen
					var coords = ol_geom.getCoordinates()[0].slice(0, -1);

					// eine neue Koordinate wurde hinzugefügt
					if (numPoints !== coords.length)
					{
						numPoints = coords.length;
					}
					else if (numPoints > 1)
					{
						var firstPoint = coords[0];
						var lastPoint = coords[coords.length - 1];
						isSnappedOnFirst = (lastPoint[0] === firstPoint[0] && lastPoint[1] === firstPoint[1]);
					}

					if (isSnappedOnFirst)
					{
						// der gesnapte Punkt (Startpunkt) ist doppelt enthalten in ol_geom => neues Polygon erzeugen
						measure_geom = new Polygon([coords]);
						self.map.showMouseTooltip(self._helpTooltipPolygonMsg);
					}
					else
					{
						// aus Polygon mache LineString
						measure_geom = new LineString(coords);
						self.map.showMouseTooltip(self._helpTooltipLineMsg);
					}
					ol_feature.set('isSnappedOnFirst', isSnappedOnFirst, true);

					self._fireGeometryChanged(measure_geom, true);
				}
			});
		}
		catch(err)
		{
			gc.gis.iface.log.error(this, err);
		}
	});

	this._draw_interaction.on(gc.gis.map.GCMapDrawInteractionEvent.DRAWEND, this, function(evt) {
		try
		{
			unByKey(featureChangeEvtentKey);

			var ol_feature = evt.feature.getImpl();
			ol_feature.unset('isSnappedOnFirst', true);

			// Unterscheidung Polygon oder Line erfolgt in Interaction-Implementierung mit Geometrie-Typ "PolygonOrLinestring"
			self._fireGeometryChanged(ol_feature.getGeometry(), false);
			self.map.showMouseTooltip(self._helpTooltipStartMsg);
		}
		catch(err)
		{
			gc.gis.iface.log.error(this, err);
		}
	});
};

/**
 * @inheritdoc
 */
gc.gis.module.OLMeasureImpl.prototype.clear = function()
{
	this.resetHighlighting();
	this._draw_interaction.clear();
};

/**
 * Erstellt ein Segment aus zwei Koordinaten.
 *
 * @protected
 * @method
 *
 * @param {ol.Coordinate} start
 * @param {ol.Coordinate} end
 * @return {gc.gis.module.GCMeasureSegmentObject}
 */
gc.gis.module.OLMeasureImpl.prototype._createSegment = function(start, end)
{
	var l = gc.gis.GCUtil.formatLength(gc.gis.OLUtil.calcLength(start, end, this.ol_map.getView().getProjection()));
	var feature = new gc.gis.feature.OLFeature(new Feature({
			text:           l.formattedValue +' '+ l.unit,
			formattedValue: l.formattedValue,
			originalValue:  l.value,
			unit:           l.unit,
			geometry:       new LineString([start, end])
		})
	);

	return {
		value: l.value,
		formattedValue: l.formattedValue,
		unit: l.unit,
		feature: feature
	};
};

/**
 * Bereitet die aktuellen Messergebnisse auf und feuert ein entsprechendes Event ab.
 *
 * @protected
 * @method
 *
 * @param {ol.geom.SimpleGeometry} ol_geom Geometrie
 * @param {boolean} is_drawing Flag, ob die Zeichnen abgeschlossen wurde
 * @fires {gc.gis.module.GCMeasure#change:measure}
 */
gc.gis.module.OLMeasureImpl.prototype._fireGeometryChanged = function(ol_geom, is_drawing)
{
	var measure = {
		area:       0,
		length:     0,
		segments:   [],
		cumulative: [],
		isDrawing:  is_drawing
	},
	coords = [],
	segment;

	if (ol_geom instanceof Polygon)
	{
		coords = ol_geom.getCoordinates()[0];
	}
	else if (ol_geom instanceof LineString)
	{
		coords = ol_geom.getCoordinates();
	}

	for (var i=0; i<coords.length - 1; i++)
	{
		segment = this._createSegment(coords[i], coords[i+1]);
		measure.segments.push(segment);
		measure.length += segment.value;
		measure.cumulative.push(gc.gis.GCUtil.formatLength(measure.length));
	}

	// finale Länge formatieren
	measure.length = gc.gis.GCUtil.formatLength(measure.length);

	// Fläche
	if(ol_geom instanceof Polygon)
	{
		measure.area = gc.gis.GCUtil.formatArea(gc.gis.OLUtil.calcArea(coords, this.ol_map.getView().getProjection()));
	}

	this._fireEvent(gc.gis.module.GCMeasureEvent.MEASURE_CHANGED, {
		measure: measure
	});
};
